import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronDown } from 'react-icons/fi';
import {
  DATE_PRESETS,
  getDateRangeDisplayText,
  validateDateRange,
  type DateRange
} from './dateRangeUtils';

interface Props {
  value?: DateRange;
  onChange: (value: DateRange) => void;
  compact?: boolean;
  showLabel?: boolean;
  disabled?: boolean;
  presets?: string[] | null;
}

/**
 * DateRangeFilter - Reusable date range filter component for dashboards
 */
const DateRangeFilter: React.FC<Props> = ({
  value = { preset: 'last_30_days', startDate: null, endDate: null },
  onChange,
  compact = false,
  showLabel = false,
  disabled = false,
  presets = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(value?.preset === 'custom');
  const [customStartDate, setCustomStartDate] = useState(value?.startDate ?? '');
  const [customEndDate, setCustomEndDate] = useState(value?.endDate ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value?.preset === 'custom') {
      setShowCustomInputs(true);
      setCustomStartDate(value.startDate ?? '');
      setCustomEndDate(value.endDate ?? '');
    } else {
      setShowCustomInputs(false);
    }
  }, [value]);

  const handlePresetSelect = (presetKey: string): void => {
    if (presetKey === 'custom') {
      setShowCustomInputs(true);
    } else {
      setShowCustomInputs(false);
      setCustomStartDate('');
      setCustomEndDate('');
      setValidationError(null);
      onChange({
        preset: presetKey,
        startDate: null,
        endDate: null
      });
      setIsOpen(false);
    }
  };

  const handleCustomDateApply = (): void => {
    const newValue: DateRange = {
      preset: 'custom',
      startDate: customStartDate,
      endDate: customEndDate
    };

    const validation = validateDateRange(newValue);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    setValidationError(null);
    onChange(newValue);
    setIsOpen(false);
  };

  const displayText = getDateRangeDisplayText(value);
  const presetEntries = presets
    ? Object.entries(DATE_PRESETS).filter(([key]) => presets.includes(key))
    : Object.entries(DATE_PRESETS);

  return (
    <div className={`relative ${compact ? '' : 'w-full'}`} ref={dropdownRef}>
      {showLabel && (
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Date Range
        </span>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2
          ${compact
            ? 'px-3 py-2 text-sm'
            : 'w-full px-4 py-2.5'
          }
          border border-gray-300 dark:border-gray-600
          rounded-lg
          bg-white dark:bg-gray-800
          text-gray-700 dark:text-gray-200
          hover:bg-gray-50 dark:hover:bg-gray-700/50
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          transition-colors duration-150
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <FiCalendar className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span className="truncate">{displayText}</span>
        <FiChevronDown
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(false); }} role="presentation"
          />
          <div
            className={`
              absolute z-20 mt-2
              ${compact ? 'right-0' : 'left-0'}
              w-72
              bg-white dark:bg-gray-800
              rounded-lg shadow-lg
              border border-gray-200 dark:border-gray-700
              overflow-hidden
            `}
          >
            <div className="py-1">
              {presetEntries.map(([key, preset]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handlePresetSelect(key)}
                  className={`
                    w-full text-left px-4 py-2.5
                    text-sm
                    transition-colors duration-150
                    ${value?.preset === key && key !== 'custom'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span>{preset.label}</span>
                    {value?.preset === key && key !== 'custom' && (
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {showCustomInputs && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <div>
                  <label htmlFor="start-date" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Start Date
                  </label>
                  <input id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      setCustomStartDate(e.target.value);
                      setValidationError(null);
                    }}
                    className="
                      w-full px-3 py-2
                      text-sm
                      border border-gray-300 dark:border-gray-600
                      rounded-md
                      bg-white dark:bg-gray-700
                      text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    "
                  />
                </div>

                <div>
                  <label htmlFor="end-date" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    End Date
                  </label>
                  <input id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      setCustomEndDate(e.target.value);
                      setValidationError(null);
                    }}
                    className="
                      w-full px-3 py-2
                      text-sm
                      border border-gray-300 dark:border-gray-600
                      rounded-md
                      bg-white dark:bg-gray-700
                      text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    "
                  />
                </div>

                {validationError && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {validationError}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleCustomDateApply}
                  disabled={!customStartDate || !customEndDate}
                  className="
                    w-full px-4 py-2
                    text-sm font-medium
                    bg-blue-600 hover:bg-blue-700
                    text-white
                    rounded-md
                    transition-colors duration-150
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
                  "
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;
